// src/components/cost-simulator/CostSimulatorTool.tsx
import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Server, Cpu, Cloud, DollarSign, Zap, TrendingUp, Calendar, RefreshCw, Link2, Users, Settings, Copy } from 'lucide-react';
import { useCostSimulatorState } from '@/hooks/useCostSimulatorState';
import { EnhancedChart } from './EnhancedChart';
import { CostInsightsTabs } from './CostInsightsTabs';
import { formatCurrency, formatTokens } from '@/lib/costCalculations';
import { aiServiceModelPresets } from '@/lib/aiServiceModels';
import type { NewSizingSnapshot } from '@/sizing/newSizingEngine';
import { useNavigate } from 'react-router-dom';
import { SizingConfigDrawer } from './SizingConfigDrawer';
import type { UseCostSimulatorSizingReturn } from '@/hooks/useCostSimulatorSizing';
interface CostSimulatorToolProps {
  snapshot: NewSizingSnapshot | null;
  modelDisplayName?: string;
  sizing?: UseCostSimulatorSizingReturn;
  onOpenDrawer?: () => void;
  onMirrorSizing?: () => void;
}

// Gradient section palettes matching TactileControls
const sectionPalette = {
  primary: {
    container: 'border-sky-500/30 bg-gradient-to-br from-sky-500/15 via-sky-500/5 to-transparent',
    icon: 'text-sky-600 dark:text-sky-400',
    iconWrap: 'bg-sky-500/15'
  },
  secondary: {
    container: 'border-emerald-500/30 bg-gradient-to-br from-emerald-500/15 via-emerald-500/5 to-transparent',
    icon: 'text-emerald-600 dark:text-emerald-400',
    iconWrap: 'bg-emerald-500/15'
  },
  success: {
    container: 'border-amber-500/30 bg-gradient-to-br from-amber-500/15 via-amber-500/5 to-transparent',
    icon: 'text-amber-600 dark:text-amber-400',
    iconWrap: 'bg-amber-500/15'
  },
  destructive: {
    container: 'border-rose-500/30 bg-gradient-to-br from-rose-500/15 via-rose-500/5 to-transparent',
    icon: 'text-rose-600 dark:text-rose-400',
    iconWrap: 'bg-rose-500/15'
  }
};
const ControlSection = ({
  icon: Icon,
  title,
  subtitle,
  color,
  children
}: {
  icon: React.ComponentType<{
    className?: string;
  }>;
  title: string;
  subtitle?: string;
  color: 'primary' | 'secondary' | 'success' | 'destructive';
  children: React.ReactNode;
}) => {
  const palette = sectionPalette[color];
  return <div className={`group relative overflow-hidden rounded-2xl border ${palette.container}`}>
      <div className="relative z-10 flex h-full flex-col justify-between rounded-2xl bg-card/60 p-4 shadow-sm backdrop-blur-sm transition-smooth sm:p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <span className={`flex h-9 w-9 items-center justify-center rounded-full ${palette.iconWrap} ring-1 ring-border/30`}>
              <Icon className={`h-4 w-4 ${palette.icon}`} />
            </span>
            <div>
              <span className="block text-sm font-semibold text-foreground">{title}</span>
              {subtitle && <span className="block text-xs text-muted-foreground">{subtitle}</span>}
            </div>
          </div>
        </div>
        <div className="mt-3 flex flex-col gap-3 sm:gap-4">{children}</div>
      </div>
    </div>;
};
export const CostSimulatorTool = ({
  snapshot,
  modelDisplayName,
  sizing,
  onOpenDrawer,
  onMirrorSizing
}: CostSimulatorToolProps) => {
  const navigate = useNavigate();
  const {
    config,
    setConfig,
    resetConfig,
    selectedModel,
    setSelectedModel,
    serverCount,
    comparison,
    monthlyTokens,
    onPremBreakdown,
    chartRollups,
    monthlyRollups
  } = useCostSimulatorState(snapshot);
  if (process.env.NODE_ENV !== 'production') {
    const crFirst = chartRollups[0];
    const crLast = chartRollups[chartRollups.length - 1];
    console.log(
      '[CostSim] render',
      `hasSnap=${Boolean(snapshot)}`,
      `gpus=${snapshot?.finalGpuCount ?? 0}`,
      `bottleneck=${snapshot?.finalBottleneck ?? 'n/a'}`,
      `chartRows=${chartRollups.length}`,
      `chartFirst=${crFirst ? `${crFirst.monthKey},cloud=${Math.round(crFirst.cumCloudUSD)},onprem=${Math.round(crFirst.cumOnPremTcoUSD)}` : 'none'}`,
      `chartLast=${crLast ? `${crLast.monthKey},cloud=${Math.round(crLast.cumCloudUSD)},onprem=${Math.round(crLast.cumOnPremTcoUSD)}` : 'none'}`,
      `monthlyRows=${monthlyRollups.length}`
    );
  }

  // Local state for editable input drafts
  const [inputCostDraft, setInputCostDraft] = useState(config.inputCostPerMToken.toString());
  const [outputCostDraft, setOutputCostDraft] = useState(config.outputCostPerMToken.toString());
  const [capexDraft, setCapexDraft] = useState(config.capexPerServer.toString());
  const [isEditingInput, setIsEditingInput] = useState(false);
  const [isEditingOutput, setIsEditingOutput] = useState(false);
  const [isEditingCapex, setIsEditingCapex] = useState(false);

  // Sync drafts when config changes externally
  useEffect(() => {
    if (!isEditingInput) setInputCostDraft(config.inputCostPerMToken.toString());
  }, [config.inputCostPerMToken, isEditingInput]);
  useEffect(() => {
    if (!isEditingOutput) setOutputCostDraft(config.outputCostPerMToken.toString());
  }, [config.outputCostPerMToken, isEditingOutput]);
  useEffect(() => {
    if (!isEditingCapex) setCapexDraft(config.capexPerServer.toString());
  }, [config.capexPerServer, isEditingCapex]);
  const hasData = snapshot && snapshot.finalGpuCount > 0;
  const commitInputCost = () => {
    const parsed = parseFloat(inputCostDraft);
    setConfig({
      inputCostPerMToken: isNaN(parsed) ? 0 : parsed
    });
    setIsEditingInput(false);
  };
  const commitOutputCost = () => {
    const parsed = parseFloat(outputCostDraft);
    setConfig({
      outputCostPerMToken: isNaN(parsed) ? 0 : parsed
    });
    setIsEditingOutput(false);
  };
  const commitCapex = () => {
    const parsed = parseInt(capexDraft.replace(/[^0-9]/g, ''), 10);
    setConfig({
      capexPerServer: isNaN(parsed) ? 0 : parsed
    });
    setIsEditingCapex(false);
  };
  return <div className="space-y-8">
      {/* Sync Banner */}
      <Card className="border-primary/30 bg-primary/5">
        
      </Card>

      {!hasData ? <Card>
          <CardContent className="py-12 text-center">
            <Server className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No Sizing Data</h3>
            <p className="text-muted-foreground mb-4">
              Run a sizing scenario on the Sizing page to see cost comparisons.
            </p>
            <Button onClick={() => navigate('/sizing')}>
              Go to Sizing Tool
            </Button>
          </CardContent>
        </Card> : <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column: TactileControls-style Configuration */}
          <div className="relative">
            {/* Gradient background wrapper */}
            <div className="absolute inset-0 rounded-[2rem] bg-gradient-to-br from-sky-500/20 via-transparent to-purple-500/20 ring-1 ring-border/30" aria-hidden />
            <div className="absolute inset-[1px] rounded-[2rem] bg-card shadow-xl backdrop-blur-xl" aria-hidden />
            
            <div className="relative z-10 flex flex-col gap-4 p-5 sm:p-6">
              {/* Header Badge */}
              <div className="flex flex-col items-center gap-2 pb-2 text-center">
                <Badge className="rounded-full border border-primary/40 bg-primary/10 px-4 py-1 text-[11px] font-semibold uppercase tracking-[0.2em] text-primary">
                  Cost Controls
                </Badge>
                <div className="text-balance text-sm text-muted-foreground">
                  Adjust parameters to compare on-prem vs cloud costs
                </div>
              </div>

              {/* Infrastructure Section (Read-only from sizing) */}
              <ControlSection icon={Server} title="Infrastructure" subtitle="From Sizing Engine" color="primary">
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-muted/50 border border-border/40 rounded-xl p-3 text-center">
                    <div className="text-2xl font-bold text-primary">{serverCount}</div>
                    <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Servers</div>
                  </div>
                  <div className="bg-muted/50 border border-border/40 rounded-xl p-3 text-center">
                    <div className="text-2xl font-bold text-primary">{snapshot?.finalGpuCount}</div>
                    <div className="text-[10px] uppercase tracking-wide text-muted-foreground">GPUs</div>
                  </div>
                </div>
                <div className="flex flex-wrap items-center justify-between gap-2 text-[11px]">
                  <span className="text-muted-foreground">Model:</span>
                  <span className="font-medium truncate max-w-[60%]">{modelDisplayName || snapshot?.modelId}</span>
                </div>
                <div className="flex flex-wrap items-center justify-between gap-2 text-[11px]">
                  <span className="text-muted-foreground">Bottleneck:</span>
                  <Badge variant="outline" className="text-[10px]">{snapshot?.finalBottleneck}</Badge>
                </div>
                
                {/* Sizing Config Buttons */}
                {(onOpenDrawer || onMirrorSizing) && <div className="flex flex-col gap-2 pt-2 border-t border-border/30">
                    {onOpenDrawer && <Button variant="outline" size="sm" onClick={onOpenDrawer} className="w-full text-xs">
                        <Settings className="h-3 w-3 mr-1.5" />
                        Configure Sizing
                      </Button>}
                    {onMirrorSizing && <Button variant="ghost" size="sm" onClick={onMirrorSizing} className="w-full text-xs">
                        <Copy className="h-3 w-3 mr-1.5" />
                        Mirror from Sizing Page
                      </Button>}
                  </div>}
              </ControlSection>

              {/* Token Throughput Section */}
              <ControlSection icon={Zap} title="Token Throughput" subtitle="Monthly volume" color="secondary">
                <div className="grid grid-cols-2 gap-3 text-[11px]">
                  <div className="space-y-1">
                    <span className="text-muted-foreground">Avg Prompt TPS</span>
                    <div className="font-mono font-medium">{(snapshot?.avgPromptTokensPerSec ?? 0).toFixed(1)}</div>
                  </div>
                  <div className="space-y-1">
                    <span className="text-muted-foreground">Avg Decode TPS</span>
                    <div className="font-mono font-medium">{(snapshot?.avgDecodeTokensPerSec ?? 0).toFixed(1)}</div>
                  </div>
                </div>
                <div className="flex items-center justify-between text-[11px] text-muted-foreground">
                  <span>Monthly Volume</span>
                  <span className="font-medium text-foreground">{formatTokens(monthlyTokens.totalTokens)}</span>
                </div>
              </ControlSection>

              {/* Cloud AI Service Section */}
              <ControlSection icon={Cloud} title="Cloud AI Service" subtitle="Token pricing" color="success">
                <div className="space-y-3">
                  <div className="flex flex-wrap items-center justify-between gap-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground/80">
                    <span>Model</span>
                    <Select value={config.aiServiceModel} onValueChange={setSelectedModel}>
                      <SelectTrigger className="h-8 w-[11rem] min-w-[9rem] text-xs rounded-full bg-background/70">
                        <SelectValue placeholder="Select model" />
                      </SelectTrigger>
                      <SelectContent>
                        {aiServiceModelPresets.map(model => <SelectItem key={model.value} value={model.value} className="text-xs">
                            {model.label}
                          </SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>

                  {selectedModel && <div className="text-[10px] text-muted-foreground">
                      Current: ${selectedModel.inputCost?.toLocaleString() ?? '—'} in / ${selectedModel.outputCost?.toLocaleString() ?? '—'} out per 1M
                    </div>}

                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground/80">
                        Input ($/1M)
                      </span>
                      <Input type="text" inputMode="decimal" value={inputCostDraft} onFocus={() => setIsEditingInput(true)} onChange={e => setInputCostDraft(e.target.value)} onBlur={commitInputCost} onKeyDown={e => {
                    if (e.key === 'Enter') commitInputCost();
                    if (e.key === 'Escape') {
                      setInputCostDraft(config.inputCostPerMToken.toString());
                      setIsEditingInput(false);
                    }
                  }} className="h-8 rounded-full bg-background/70 text-xs" />
                    </div>
                    <div className="space-y-1">
                      <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground/80">
                        Output ($/1M)
                      </span>
                      <Input type="text" inputMode="decimal" value={outputCostDraft} onFocus={() => setIsEditingOutput(true)} onChange={e => setOutputCostDraft(e.target.value)} onBlur={commitOutputCost} onKeyDown={e => {
                    if (e.key === 'Enter') commitOutputCost();
                    if (e.key === 'Escape') {
                      setOutputCostDraft(config.outputCostPerMToken.toString());
                      setIsEditingOutput(false);
                    }
                  }} className="h-8 rounded-full bg-background/70 text-xs" />
                    </div>
                  </div>
                </div>
              </ControlSection>

              {/* On-Prem Costs Section */}
              <ControlSection icon={DollarSign} title="On-Prem Costs" subtitle="CapEx & OpEx" color="destructive">
                <div className="space-y-3">
                  <div className="space-y-1">
                    <div className="flex items-center justify-between text-[10px] font-semibold uppercase tracking-wide text-muted-foreground/80">
                      <span>CapEx per Server</span>
                      <span className="text-foreground">${parseInt(capexDraft || '0').toLocaleString()}</span>
                    </div>
                    <Input type="text" inputMode="numeric" value={capexDraft} onFocus={() => setIsEditingCapex(true)} onChange={e => setCapexDraft(e.target.value.replace(/[^0-9]/g, ''))} onBlur={commitCapex} onKeyDown={e => {
                  if (e.key === 'Enter') commitCapex();
                  if (e.key === 'Escape') {
                    setCapexDraft(config.capexPerServer.toString());
                    setIsEditingCapex(false);
                  }
                }} className="h-8 rounded-full bg-background/70 text-xs" />
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-1">
                      <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground/80">
                        Power kW/srv
                      </span>
                      <Input type="number" step="0.1" value={config.powerKwPerServer} onChange={e => setConfig({
                    powerKwPerServer: parseFloat(e.target.value) || 0
                  })} className="h-8 rounded-full bg-background/70 text-xs" />
                    </div>
                    <div className="space-y-1">
                      <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground/80">
                        $/kWh
                      </span>
                      <Input type="number" step="0.01" value={config.electricityPerKwh} onChange={e => setConfig({
                    electricityPerKwh: parseFloat(e.target.value) || 0
                  })} className="h-8 rounded-full bg-background/70 text-xs" />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-1">
                      <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground/80">
                        Admin $/mo
                      </span>
                      <Input type="number" value={config.adminPerServerMonthly} onChange={e => setConfig({
                    adminPerServerMonthly: parseFloat(e.target.value) || 0
                  })} className="h-8 rounded-full bg-background/70 text-xs" />
                    </div>
                    <div className="space-y-1">
                      <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground/80">
                        Facilities
                      </span>
                      <Input type="number" value={config.facilitiesMonthly} onChange={e => setConfig({
                    facilitiesMonthly: parseFloat(e.target.value) || 0
                  })} className="h-8 rounded-full bg-background/70 text-xs" />
                    </div>
                  </div>
                </div>
              </ControlSection>

              {/* Growth Section */}
              <ControlSection icon={TrendingUp} title="Growth Rate" subtitle="5-year projection" color="secondary">
                <div className="space-y-3">
                  <div className="flex items-center justify-between text-[11px] font-semibold uppercase tracking-wide text-muted-foreground/80">
                    <span>Annual Growth</span>
                    <span className="text-foreground">{config.annualGrowthRate}%</span>
                  </div>
                  <Slider value={[config.annualGrowthRate]} onValueChange={([v]) => setConfig({
                annualGrowthRate: v
              })} min={0} max={100} step={5} />
                  <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-1">
                      <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground/80">
                        Work hrs/day
                      </span>
                      <Input type="number" value={config.workHoursPerDay} onChange={e => setConfig({
                    workHoursPerDay: parseInt(e.target.value) || 8
                  })} className="h-8 rounded-full bg-background/70 text-xs" />
                    </div>
                    <div className="space-y-1">
                      <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground/80">
                        Days/month
                      </span>
                      <Input type="number" value={config.workDaysPerMonth} onChange={e => setConfig({
                    workDaysPerMonth: parseInt(e.target.value) || 22
                  })} className="h-8 rounded-full bg-background/70 text-xs" />
                    </div>
                  </div>
                </div>
              </ControlSection>

              {/* Reset Button */}
              <Button variant="outline" size="sm" className="w-full rounded-full" onClick={resetConfig}>
                <RefreshCw className="h-3 w-3 mr-2" />
                Reset to Defaults
              </Button>
            </div>
          </div>

          {/* Right Column: Results */}
          <div className="lg:col-span-2 space-y-6">
            {/* Hero KPIs */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Card className="bg-gradient-to-br from-primary/10 to-primary/5 border-primary/20">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-2 mb-2">
                    <Calendar className="h-4 w-4 text-primary" />
                    <span className="text-sm text-muted-foreground">Break-even</span>
                  </div>
                  <div className="text-2xl font-bold">
                    {comparison.breakEvenMonth || 'N/A'}
                  </div>
                  {comparison.breakEvenMonthIndex !== null && <div className="text-xs text-muted-foreground mt-1">
                      Month {comparison.breakEvenMonthIndex + 1}
                    </div>}
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-destructive/10 to-destructive/5 border-destructive/20">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-2 mb-2">
                    <Server className="h-4 w-4 text-destructive" />
                    <span className="text-sm text-muted-foreground">5-Year On-Prem</span>
                  </div>
                  <div className="text-2xl font-bold">
                    {formatCurrency(comparison.fiveYearOnPremTco, true)}
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    Total Cost of Ownership
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-muted/50 to-muted/30 border-muted-foreground/20">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-2 mb-2">
                    <Cloud className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">5-Year Cloud</span>
                  </div>
                  <div className="text-2xl font-bold">
                    {formatCurrency(comparison.fiveYearCloudSpend, true)}
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    Cumulative Spend
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Savings Badge */}
            {comparison.savings > 0 && <div className="flex justify-center">
                <Badge className="bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 border-emerald-500/30 px-4 py-2 text-sm">
                  <TrendingUp className="h-4 w-4 mr-2" />
                  {formatCurrency(comparison.savings, true)} potential savings ({comparison.savingsPercent.toFixed(0)}%)
                </Badge>
              </div>}

            {/* Crossover Chart */}
            <Card className="overflow-hidden">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <TrendingUp className="h-4 w-4" />
                  Cumulative Cost Comparison
                </CardTitle>
                <p className="text-sm text-muted-foreground">
                  On-prem vs cloud costs over 60 months
                </p>
              </CardHeader>
              <CardContent>
                <div className="h-[350px]">
                  <EnhancedChart monthlyRollups={chartRollups} />
                </div>
              </CardContent>
            </Card>

            {/* Advanced Insights Tabs */}
            <CostInsightsTabs snapshot={snapshot} config={config} monthlyRollups={monthlyRollups} comparison={comparison} monthlyTokens={monthlyTokens} onPremBreakdown={onPremBreakdown} serverCount={serverCount} />
          </div>
        </div>}
    </div>;
};
